import React from 'react';
import { cn } from '@/lib/utils';
import { Star, CheckCircle } from 'lucide-react';
import { EditorField } from '@/config/ecoBuilder/types';
import { usePageContext } from '@/components/ecoBuilder/renderer/PageContext';
import { RichTextRenderer } from '@/components/ecoBuilder/atoms/RichTextRenderer';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation, Autoplay } from 'swiper/modules';
import Image from 'next/image';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

// 1. The Interface
export interface SocialProofReviewsProps {
    variant?: 'whatsapp' | 'cards' | 'slider' | 'modern_list' | 'elegant_grid';
    title?: string;
    description?: string;
    reviews?: {
        id: string;
        name: string;
        wilaya: string;
        rating: number;
        text: string;
        image?: string;
        date?: string;
    }[];
    starsColor?: string;
    cardBackgroundColor?: string;
    textColor?: string;
    sectionBackgroundColor?: string;
    className?: string;
    style?: React.CSSProperties;
}

// 2. The Configuration Schema
export const SocialProofReviewsSchema: EditorField[] = [
    {
        key: 'reviews',
        label: 'التقييمات',
        type: 'array',
        arrayConfig: {
            itemLabel: 'تقييم',
            schema: [
                { key: 'name', label: 'الاسم', type: 'text', defaultValue: 'عميل' },
                { key: 'wilaya', label: 'الولاية/المدينة', type: 'text', defaultValue: 'الجزائر' },
                { key: 'rating', label: 'التقييم', type: 'number', defaultValue: 5, options: [] }, // options ignored for number but kept for consistency if needed later
                { key: 'text', label: 'نص التقييم', type: 'textarea', defaultValue: 'منتج ممتاز!' },
                { key: 'date', label: 'التاريخ', type: 'text', defaultValue: 'منذ يومين' },
                { key: 'image', label: 'صورة العميل (اختياري)', type: 'image' }
            ]
        },
        defaultValue: [
            { id: 'rev_1', name: 'أحمد. م', wilaya: 'الجزائر', rating: 5, text: 'منتج رائع وصلني في الوقت المناسب، شكراً لكم!', date: 'منذ يومين' },
            { id: 'rev_2', name: 'سارة. ب', wilaya: 'وهران', rating: 5, text: 'الجودة ممتازة أفضل مما توقعت.', date: 'منذ أسبوع' },
            { id: 'rev_3', name: 'كريم. س', wilaya: 'سطيف', rating: 4, text: 'خدمة التوصيل سريعة جداً.', date: 'منذ أسبوعين' },
        ]
    },
    {
        key: 'variant',
        label: 'نمط العرض',
        type: 'select',
        options: [
            { value: 'modern_list', label: 'قائمة عصرية (Modern List)' },
            { value: 'whatsapp', label: 'محادثات واتساب (الأكثر ثقة)' },
            { value: 'cards', label: 'بطاقات شبكية' },
            { value: 'slider', label: 'شريط متحرك' },
            { value: 'elegant_grid', label: 'شبكة أنيقة (Elegant Grid)' }
        ],
        defaultValue: 'modern_list'
    },
    {
        key: 'title',
        label: 'العنوان الرئيسي',
        type: 'text',
        defaultValue: 'ماذا يقول عملاؤنا؟'
    },
    {
        key: 'description',
        label: 'وصف فرعي',
        type: 'richtext',
        defaultValue: ''
    },
    {
        key: 'starsColor',
        label: 'لون النجوم',
        type: 'color',
    },
    {
        key: 'textColor',
        label: 'لون النصوص',
        type: 'color',
    },
    {
        key: 'cardBackgroundColor',
        label: 'لون خلفية البطاقات',
        type: 'color',
        defaultValue: '#ffffff'
    },
    {
        key: 'sectionBackgroundColor',
        label: 'لون خلفية القسم',
        type: 'color',
        defaultValue: 'transparent'
    }
];

export const SocialProofReviewsDefaultProps: Partial<SocialProofReviewsProps> = {
    variant: 'modern_list',
    title: 'ماذا يقول عملاؤنا؟',
    description: '',
    starsColor: '#fbbf24',
    textColor: '#1f2937',
    cardBackgroundColor: '#ffffff',
    sectionBackgroundColor: 'transparent',
    reviews: [
        { id: '1', name: 'أحمد. م', wilaya: 'الجزائر', rating: 5, text: 'منتج رائع وصلني في الوقت المناسب، شكراً لكم!', date: 'منذ يومين' },
        { id: '2', name: 'سارة. ب', wilaya: 'وهران', rating: 5, text: 'الجودة ممتازة أفضل مما توقعت.', date: 'منذ أسبوع' },
        { id: '3', name: 'كريم. س', wilaya: 'سطيف', rating: 4, text: 'خدمة التوصيل سريعة جداً.', date: 'منذ أسبوعين' },
    ]
};

// 3. The Component Implementation
export const SocialProofReviews: React.FC<SocialProofReviewsProps> = ({
    variant = 'modern_list',
    title,
    description,
    reviews = [],
    starsColor,
    textColor,
    cardBackgroundColor = '#ffffff',
    sectionBackgroundColor = 'transparent',
    style,
    className
}) => {
    const { themeSettings } = usePageContext();
    const resolvedStarsColor = (!starsColor || starsColor === '#fbbf24') ? (themeSettings?.primaryColor || '#fbbf24') : starsColor;
    const resolvedTextColor = (!textColor || textColor === '#1f2937') ? (themeSettings?.textColor || '#1f2937') : textColor;


    // WhatsApp Variant
    if (variant === 'whatsapp') {
        const bg = sectionBackgroundColor;

        return (
            <div className={cn("w-full py-8", className)} style={{ backgroundColor: bg, ...style }} dir="rtl">
                <div className="max-w-md mx-auto px-4">
                    {title && <div className="text-center font-bold mb-2 text-xl [&_p]:m-0" style={{ color: resolvedTextColor }} dangerouslySetInnerHTML={{ __html: title }} />}
                    {description && <div className="text-center mb-6 opacity-80" style={{ color: resolvedTextColor }} dangerouslySetInnerHTML={{ __html: description }} />}

                    <div className="space-y-4">
                        {reviews.map((review) => (
                            <div
                                key={review.id}
                                // Mobile centering: mx-auto on small screens, ml-12 on medium+ for the staggered look if desired, or just consistent.
                                // User asked for "Reviews in the middle exactly" on mobile.
                                // Removing ml-12 for mobile, keeping it for desktop if needed, or just simplifying to mx-auto for a centered stack.
                                className="flex flex-col rounded-lg p-3 shadow-sm relative mx-auto w-full md:w-[90%] lg:w-[85%] animate-in slide-in-from-bottom-2 duration-500"
                                style={{ backgroundColor: cardBackgroundColor }}
                            >
                                {/* Triangle arrow - adjust position for centered look or keep right */}
                                <div
                                    className="absolute top-0 -right-2 w-0 h-0 border-[10px] border-transparent border-t-white drop-shadow-sm hidden md:block"
                                    style={{
                                        transform: 'rotate(-90deg)',
                                        borderTopColor: cardBackgroundColor
                                    }}
                                />

                                <div className="flex justify-between items-start border-b border-gray-100 pb-2 mb-2">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600 overflow-hidden">
                                            {review.image ? <img src={review.image} alt="" className="w-full h-full object-cover" /> : (review.name?.charAt(0) || '?')}
                                        </div>
                                        <div>
                                            <div className="text-sm font-bold text-[#075E54] [&_p]:m-0" dangerouslySetInnerHTML={{ __html: review.name || 'عميل' }} />
                                            <div className="text-[10px] text-gray-400 [&_p]:m-0" dangerouslySetInnerHTML={{ __html: review.wilaya || '' }} />
                                        </div>
                                    </div>
                                    <div className="flex gap-0.5">
                                        {Array.from({ length: 5 }).map((_, i) => (
                                            <Star
                                                key={i}
                                                className={cn("w-3 h-3", i < (review.rating || 0) ? "fill-current" : "text-gray-200")}
                                                style={{ color: i < (review.rating || 0) ? resolvedStarsColor : undefined }}
                                            />
                                        ))}
                                    </div>
                                </div>

                                <div className="text-sm leading-relaxed [&_p]:m-0" style={{ color: '#111827' }} dangerouslySetInnerHTML={{ __html: review.text || '' }} />

                                <div className="flex justify-between items-end mt-2">
                                    <span className="text-[10px] text-gray-400">{review.date}</span>
                                    <CheckCircle className="w-4 h-4 text-[#34B7F1]" aria-label="Verified" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    // Modern List Variant (New)
    if (variant === 'modern_list') {
        return (
            <div className={cn("w-full py-10", className)} style={{ backgroundColor: sectionBackgroundColor, ...style }} dir="rtl">
                <div className="max-w-md mx-auto px-4">
                    <div className="text-center mb-8">
                        {title && <RichTextRenderer html={title} className="text-2xl font-bold mb-2" style={{ color: resolvedTextColor }} />}
                        {description && <RichTextRenderer html={description} className="opacity-70 text-sm" style={{ color: resolvedTextColor }} />}
                    </div>

                    <div className="space-y-6">
                        {reviews.map((review, idx) => (
                            <div
                                key={idx}
                                className="relative flex flex-col items-center text-center p-6 rounded-2xl border border-slate-100 shadow-sm transition-transform hover:scale-[1.02] bg-white"
                                style={{ backgroundColor: cardBackgroundColor }}
                            >
                                <div className="absolute -top-4 w-12 h-12 rounded-full border-4 border-white shadow-sm overflow-hidden bg-slate-100 flex items-center justify-center">
                                    {review.image ? <img src={review.image} alt="" className="w-full h-full object-cover" /> : <span className="text-lg font-bold text-slate-400">{review.name?.charAt(0)}</span>}
                                </div>

                                <div className="mt-6 mb-3 flex justify-center gap-1">
                                    {Array.from({ length: 5 }).map((_, i) => (
                                        <Star key={i} className={cn("w-4 h-4", i < (review.rating || 0) ? "fill-current" : "text-slate-200")} style={{ color: i < (review.rating || 0) ? resolvedStarsColor : undefined }} />
                                    ))}
                                </div>

                                <RichTextRenderer html={review.text || ''} className="text-sm leading-relaxed mb-4 opacity-80" style={{ color: resolvedTextColor }} />

                                <div className="flex flex-col items-center">
                                    <span className="font-bold text-sm" style={{ color: resolvedTextColor }}>{review.name}</span>
                                    <span className="text-xs text-muted-foreground">{review.wilaya}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    // Elegant Grid Variant (New)
    if (variant === 'elegant_grid') {
        return (
            <div className={cn("w-full py-12", className)} style={{ backgroundColor: sectionBackgroundColor, ...style }} dir="rtl">
                <div className="max-w-6xl mx-auto px-4">
                    <div className="text-center mb-12">
                        {title && <RichTextRenderer html={title} className="text-3xl font-extrabold tracking-tight mb-3" style={{ color: resolvedTextColor }} />}
                        {description && <RichTextRenderer html={description} className="opacity-60 max-w-2xl mx-auto" style={{ color: resolvedTextColor }} />}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {reviews.map((review, idx) => (
                            <div
                                key={idx}
                                className="group p-6 rounded-xl border border-transparent hover:border-slate-100 hover:shadow-xl transition-all duration-300 relative overflow-hidden"
                                style={{ backgroundColor: cardBackgroundColor }}
                            >
                                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-primary/5 to-transparent rounded-bl-3xl -mr-2 -mt-2 group-hover:scale-110 transition-transform"></div>

                                <div className="relative z-10">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center shadow-inner overflow-hidden">
                                                {review.image ? <img src={review.image} alt="" className="w-full h-full object-cover" /> : <span className="font-bold text-slate-600">{review.name?.charAt(0)}</span>}
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-sm" style={{ color: resolvedTextColor }}>{review.name}</h4>
                                                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{review.wilaya}</span>
                                            </div>
                                        </div>
                                        <div className="flex gap-0.5">
                                            {Array.from({ length: 5 }).map((_, i) => (
                                                <Star key={i} className={cn("w-3 h-3", i < (review.rating || 0) ? "fill-current" : "text-slate-100")} style={{ color: i < (review.rating || 0) ? resolvedStarsColor : undefined }} />
                                            ))}
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <RichTextRenderer html={review.text || ''} className="text-sm leading-6 opacity-80" style={{ color: resolvedTextColor }} />
                                    </div>

                                    <div className="flex items-center gap-2 text-xs text-green-600 bg-green-50 w-fit px-2 py-1 rounded-full">
                                        <CheckCircle className="w-3 h-3" />
                                        <span>عملية شراء مؤكدة</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    // Cards Variant (Optimized)
    if (variant === 'cards') {
        return (
            <div className={cn("w-full py-16", className)} style={{ backgroundColor: sectionBackgroundColor, ...style }} dir="rtl">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        {title && <RichTextRenderer html={title} className="text-3xl md:text-4xl font-bold mb-4 tracking-tight" style={{ color: resolvedTextColor }} />}
                        {description && <RichTextRenderer html={description} className="opacity-70 max-w-2xl mx-auto text-lg" style={{ color: resolvedTextColor }} />}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {reviews.map((review) => (
                            <div
                                key={review.id}
                                className="group flex flex-col h-full border border-slate-100 rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 bg-white"
                                style={{ backgroundColor: cardBackgroundColor }}
                            >
                                <div className="flex items-center justify-between mb-6">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-full bg-slate-50 border border-slate-100 flex items-center justify-center text-lg font-bold text-slate-700 overflow-hidden shadow-inner">
                                            {review.image ? <img src={review.image} alt="" className="w-full h-full object-cover" /> : (review.name?.charAt(0) || '?')}
                                        </div>
                                        <div>
                                            <RichTextRenderer html={review.name || 'عميل'} className="font-bold text-base leading-tight" style={{ color: resolvedTextColor }} />
                                            <div className="text-xs text-muted-foreground mt-0.5">{review.wilaya}</div>
                                        </div>
                                    </div>
                                    <div className="flex gap-0.5 bg-yellow-50/50 px-2 py-1 rounded-full border border-yellow-100/50">
                                        {Array.from({ length: 5 }).map((_, i) => (
                                            <Star
                                                key={i}
                                                className={cn("w-3.5 h-3.5", i < (review.rating || 0) ? "fill-yellow-400 text-yellow-400" : "text-gray-200")}
                                            />
                                        ))}
                                    </div>
                                </div>

                                <div className="flex-grow">
                                    <RichTextRenderer html={review.text || ''} className="text-base leading-relaxed opacity-80" style={{ color: resolvedTextColor }} />
                                </div>

                                <div className="mt-6 pt-6 border-t border-slate-50 flex items-center justify-between">
                                    <div className="flex items-center gap-2 text-xs font-medium text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-full">
                                        <CheckCircle className="w-3.5 h-3.5" />
                                        <span>شراء مؤكد</span>
                                    </div>
                                    <span className="text-xs text-muted-foreground opacity-60">{review.date}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    // Slider Variant (Default Fallback)
    const contentBg = cardBackgroundColor;

    return (
        <div className={cn("w-full py-12", className)} style={{ backgroundColor: sectionBackgroundColor, ...style }} dir="rtl">
            <div className="max-w-6xl mx-auto px-4">
                <div className="text-center mb-10">
                    {title && <RichTextRenderer html={title} className="text-3xl font-bold mb-4" style={{ color: resolvedTextColor }} />}
                    {description && <RichTextRenderer html={description} className="opacity-80 max-w-2xl mx-auto" style={{ color: resolvedTextColor }} />}
                </div>

                <Swiper
                    modules={[Pagination, Navigation, Autoplay]}
                    spaceBetween={20}
                    slidesPerView={1}
                    navigation
                    pagination={{ clickable: true }}
                    autoplay={{
                        delay: 3000,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true
                    }}
                    breakpoints={{
                        640: { slidesPerView: 2 },
                        1024: { slidesPerView: 3 },
                    }}
                    className="pb-12"
                >
                    {reviews.map((review, idx) => (
                        <SwiperSlide key={idx}>
                            <div className="h-full rounded-xl p-6 relative flex flex-col" style={{ backgroundColor: contentBg, color: resolvedTextColor }}>
                                {/* Stars */}
                                <div className="flex items-center gap-1 mb-4 text-yellow-400">
                                    {Array.from({ length: 5 }).map((_, i) => (
                                        <Star key={i} className="w-4 h-4 fill-current" />
                                    ))}
                                </div>

                                {/* Review Text */}
                                <RichTextRenderer html={review.text || ''} className="mb-6 leading-relaxed opacity-90 flex-grow" />

                                {/* Author */}
                                <div className="flex items-center gap-3 mt-auto pt-4 border-t border-white/10">
                                    {review.image ? (
                                        <div className="relative w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                                            <Image
                                                src={review.image}
                                                alt={review.name || "Reviewer"}
                                                fill
                                                className="object-cover"
                                                unoptimized
                                            />
                                        </div>
                                    ) : (
                                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                            <span className="font-bold text-primary">{review.name?.[0] || '?'}</span>
                                        </div>
                                    )}
                                    <div>
                                        <RichTextRenderer html={review.name || 'عميل'} className="font-bold text-sm" inline />
                                        {review.wilaya && <RichTextRenderer html={review.wilaya} className="text-xs opacity-70" inline />}
                                    </div>
                                </div>
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
        </div>
    );
};
